import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { api } from "@/lib/api";

// The Program has two faces:
//  • PUBLIC PROMO (homepage, default): a spoiler-free teaser — oversized THE PROGRAM,
//    small PROGRAM 001, one hook, date, "with Shi". No segment list, no vinyl specs.
//  • EMBEDDED (a Program detail page, after air): the full rundown from that
//    edition's chapters, where spoilers no longer matter.
const Row = ({ seg, i }) => (
  <motion.div
    data-testid={`program-row-${seg.n}`}
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className="group grid grid-cols-[auto_1fr_auto] items-baseline gap-5 border-t border-gold/25 py-6 md:gap-10 md:py-7"
  >
    <span className="font-sans text-sm font-bold tabular-nums tracking-[0.2em] text-gold md:text-base">
      {seg.n}
    </span>
    <h3 className="font-display text-2xl font-bold leading-none tracking-tight text-cream transition-colors duration-500 group-hover:text-neon md:text-4xl">
      {seg.title}
    </h3>
    <span className="text-right font-sans text-[10px] uppercase tracking-[0.28em] text-cream/50 md:text-xs">
      {seg.meta}
    </span>
  </motion.div>
);

export default function TheProgram({
  segments,
  episodeLabel = "Program 001",
  date,
  embedded = false,
  intro,
}) {
  const [dateLabel, setDateLabel] = useState(null);

  useEffect(() => {
    const fmt = (v) => {
      try {
        return format(new Date(v), "EEEE · MMMM d").toUpperCase();
      } catch {
        return null;
      }
    };
    if (date) {
      setDateLabel(fmt(date));
      return;
    }
    api
      .get("/premiere")
      .then((r) => r?.premiere_at && setDateLabel(fmt(r.premiere_at)))
      .catch(() => {});
  }, [date]);

  // EMBEDDED — post-air rundown on the Program detail page
  if (embedded) {
    const rows = segments || [];
    return (
      <div
        data-testid="the-program-section"
        className="relative overflow-hidden rounded-3xl border border-gold/25 bg-cocoa"
      >
        <div className="relative p-7 md:p-12">
          <div className="flex flex-wrap items-end justify-between gap-6 border-b border-gold/40 pb-8">
            <div>
              <h2 className="mt-3 font-display text-4xl font-black uppercase leading-[0.82] tracking-tight text-cream md:text-6xl">
                The Program
              </h2>
            </div>
            <div className="text-right font-sans text-[10px] uppercase leading-relaxed tracking-[0.28em] text-cream/70 md:text-xs">
              {dateLabel && <p className="text-gold">{dateLabel}</p>}
              <p className="mt-1">{episodeLabel}</p>
            </div>
          </div>
          {intro && (
            <p className="mt-6 max-w-lg font-editorial text-lg italic leading-relaxed text-cream/70">
              {intro}
            </p>
          )}
          <div className="mt-2 border-b border-gold/25">
            {rows.map((seg, i) => (
              <Row key={`${seg.n}-${seg.title}`} seg={seg} i={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // PUBLIC PROMO — spoiler-free teaser
  return (
    <section
      data-testid="the-program-section"
      className="relative overflow-hidden bg-cocoa py-24 md:py-32"
    >
      <div className="glow-ember pointer-events-none absolute left-1/2 top-0 h-[26rem] w-[26rem] -translate-x-1/2" aria-hidden="true" />
      <div className="relative mx-auto max-w-4xl px-5 text-center md:px-10">
        <p className="mt-6 font-sans text-xs font-bold uppercase tracking-[0.4em] text-ember">
          {episodeLabel}
        </p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 font-display text-6xl font-black uppercase leading-[0.84] tracking-tight text-cream md:text-8xl"
        >
          The Program
        </motion.h2>
        <div className="mx-auto mt-8 h-px w-16 bg-gold/50" aria-hidden="true" />
        <p className="mx-auto mt-8 max-w-xl font-editorial text-2xl italic leading-relaxed text-cream/85 md:text-3xl">
          The first episode is where we start. Pop culture, side conversations,
          and the part everybody keeps talking around.
        </p>
        <p className="mt-10 font-sans text-[11px] font-bold uppercase tracking-[0.3em] text-latte">
          {dateLabel || "Premieres Soon"}{" "}
          <span className="text-neon">&middot; with SHi</span>
        </p>
      </div>
    </section>
  );
}
